import { createContext, useContext, useEffect, useState } from 'react';
import { createUserApi } from './api';
import { useLanguage } from './LanguageContext';
import { useVersion } from '../components/common/commonHooks';
import { useLocation, useNavigate } from 'react-router-dom';

import { useClientParams } from './AppConfigContext';
import { useFirebase } from './firebase';

const AuthContext = createContext();

//TODO: force certain flows always? e.g. email verification, onboarding, so that if a
// user comes through another url it will still re-route them to appropriate account setup
const AuthProvider = ({ children }) => {
  const params = useClientParams();
  const firebase = useFirebase(params, async () => await refreshLoginStatus());

  const { currentLanguage, setCurrentLanguage } = useLanguage();
  const [dataVersion, incrementDataVersion] = useVersion();
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [api] = useState(
    createUserApi(firebase.getToken, async () => {
      await refreshLoginStatus();
      incrementDataVersion();
    }),
  );
  const [user, setUser] = useState(null);
  const [userEmployments, setUserEmployments] = useState(null);
  const [administratedEmployers, setAdministratedEmployers] = useState(null);
  const navigate = useNavigate();

  const updateUser = async () => {
    if ((await firebase.isLoggedIn()) && (await firebase.userEmailIsVerified())) {
      const user = await api.getUser();
      if (user && user.found !== false) {
        setUser(user);
        setUserEmployments(await api.getUserEmployments());
        if (user.isPayrollAdmin) {
          setAdministratedEmployers(await api.getAdministratedEmployers());
        }
        return user;
      }
    }
    return null;
  };

  const refreshLoginStatus = async () => {
    const user = await updateUser();
    if (user) {
      if (!isLoggedIn) {
        setCurrentLanguage(user.languagePreference);
      }
      setIsLoggedIn(true);
      return user;
    }

    setUser(null);
    setIsLoggedIn(false);
    return null;
  };

  const handleNavigation = async () => {
    if (await firebase.isLoggedIn()) {
      const user = await refreshLoginStatus();
      const { activationCode } = { ...JSON.parse(localStorage.getItem('activationCode')) };

      if (!(await firebase.userEmailIsVerified())) {
        await firebase.sendEmailVerification();
        navigate('/verifyEmail');
      } else {
        // at this point the redirect will either be used or forgotten
        const redirectTo = localStorage.getItem('redirectTo');
        localStorage.removeItem('redirectTo');

        const isOnboarding = !user.onboardingComplete;
        const { onboardingRedirect, accountRedirect } = user.isPayrollAdmin
          ? {
              onboardingRedirect: '/admin/onboarding',
              accountRedirect: '/admin/dashboard',
            }
          : {
              onboardingRedirect: `/onboarding${activationCode ? '/' + activationCode : ''}`,
              accountRedirect: '/account',
            };

        const navigateTo = isOnboarding ? onboardingRedirect : redirectTo ?? accountRedirect;

        navigate(navigateTo);
      }
    } else {
      navigate('/');
    }
  };

  const handleAccountCreationAndNavigation = async ({ isPayrollAdmin, activationCode }) => {
    localStorage.setItem('activationCode', JSON.stringify({ activationCode }));
    await api.createAccount({ isPayrollAdmin, languagePreference: currentLanguage, activationCode });
    await handleNavigation();
  };

  const createUserWithEmailAndPassword = async ({ email, password, isPayrollAdmin, activationCode }) => {
    await firebase.createUserWithEmailAndPassword(email, password);
    await handleAccountCreationAndNavigation({ isPayrollAdmin, activationCode });
  };

  const createAccountWithGoogle = async ({ isPayrollAdmin, activationCode }) => {
    await firebase.signInWithGoogle();
    await handleAccountCreationAndNavigation({ isPayrollAdmin, activationCode });
  };

  const logInWithEmailAndPassword = async ({ email, password }) => {
    await firebase.signInWithEmailAndPassword(email, password);
    await handleNavigation();
  };

  const logInWithGoogle = async () => {
    await firebase.signInWithGoogle();
    await handleNavigation();
  };

  const logOut = async () => {
    await firebase.signOut();
    await handleNavigation();
  };

  if (!firebase.isReady) return null;

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        userEmployments,
        administratedEmployers,
        createUserWithEmailAndPassword,
        createAccountWithGoogle,
        logInWithEmailAndPassword,
        logInWithGoogle,
        logOut,
        api,
        dataVersion,

        userEmail: firebase.userEmail,
        userEmailIsVerified: firebase.userEmailIsVerified,
        sendEmailVerification: firebase.sendEmailVerification,
        updateUserEmail: firebase.updateUserEmail,
        updateUserPassword: firebase.updateUserPassword,
        sendPasswordResetEmail: firebase.sendPasswordResetEmail,
        confirmPasswordReset: firebase.confirmPasswordReset,
        userAuthenticatedUsingPassword: firebase.userAuthenticatedUsingPassword,
        handleNavigation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const AuthRequired = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isLoggedIn === false) {
      localStorage.setItem('redirectTo', location.pathname);
      navigate('/login');
    }
  }, [auth.isLoggedIn, location, navigate]);

  return auth.isLoggedIn === true && <>{children}</>;
};

export { AuthProvider, useAuth, AuthRequired };
